import { useState } from 'react';
import { invoiceNumber, invoiceSeries } from './accessKey';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../shared/auth/AuthContext';
import { ApiError } from '../../shared/api/client';
import { useToast } from '@nexora/design-system';
import { useFiscalInvoice, useCancelFiscalInvoice } from './useFiscalInvoices';
import type { Invoice } from './useFiscalInvoices';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Modal,
  Table,
  Select,
  Textarea,
  LoadingSkeleton,
} from '@nexora/design-system';

/* ── Helpers ── */

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCnpj(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 14) {
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }
  return value;
}

function formatCpf(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  }
  return value;
}

function formatAccessKey(value: string): string {
  // Display key in groups of 4 digits separated by spaces
  return value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
}

const statusVariant: Record<string, 'info' | 'warning' | 'success' | 'error' | 'neutral'> = {
  DRAFT: 'neutral',
  PENDING: 'warning',
  AUTHORIZED: 'success',
  DENIED: 'error',
  REJECTED: 'error',
  CANCELLED: 'error',
  QUEUED: 'info',
};

const statusLabel: Record<string, string> = {
  DRAFT: 'Rascunho',
  PENDING: 'Pendente',
  AUTHORIZED: 'Autorizado',
  DENIED: 'Negado',
  REJECTED: 'Rejeitado',
  CANCELLED: 'Cancelado',
  QUEUED: 'Na fila',
};

const typeLabel: Record<string, string> = {
  NFE: 'NF-e',
  NFSE: 'NFS-e',
  NFCE: 'NFC-e',
};

const cancelReasons = [
  { value: 'erro_emissao', label: 'Erro na emissão' },
  { value: 'solicitacao_cliente', label: 'Cancelamento por solicitação do cliente' },
  { value: 'erro_dados', label: 'Cancelamento por erro de dados' },
  { value: 'outro', label: 'Outro' },
];

const cancellableStatuses = ['DRAFT', 'PENDING', 'AUTHORIZED'];

/* ── Component ── */

export function FiscalInvoiceDetailPage(): JSX.Element {
  const { id } = useParams({ from: '/app/fiscal/invoices/$id' });
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const { addToast } = useToast();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelJustification, setCancelJustification] = useState('');

  /* ── Queries ── */

  const {
    data: invoice,
    isLoading,
    error,
    refetch,
  } = useFiscalInvoice(tenant?.id ?? '', id);

  /* ── Cancel mutation ── */

  const cancelMutation = useCancelFiscalInvoice(tenant?.id ?? '');

  const handleCancelConfirm = (): void => {
    if (!cancelReason || !cancelJustification.trim()) return;
    cancelMutation.mutate(
      { id, reason: cancelReason, justification: cancelJustification },
      {
        onSuccess: () => {
          setShowCancelModal(false);
          setCancelReason('');
          setCancelJustification('');
          addToast('Documento fiscal cancelado com sucesso', 'success');
        },
        onError: (err: Error) => {
          const message = err instanceof ApiError ? err.message : 'Erro ao cancelar documento fiscal';
          addToast(message, 'error');
        },
      },
    );
  };

  /* ── Data checks ── */

  const invoiceData = invoice ?? (null as Invoice | null);
  const canCancel = invoiceData ? cancellableStatuses.includes(invoiceData.status) : false;

  /* ── Items table columns ── */

  const itemsColumns = [
    { header: 'Descrição', accessor: 'description' as const },
    { header: 'NCM', accessor: 'ncm' as const },
    { header: 'CFOP', accessor: 'cfop' as const },
    { header: 'CST', accessor: 'cst' as const },
    {
      header: 'Qtd',
      accessor: 'quantity' as const,
      align: 'right' as const,
      render: (value: number) => value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
    },
    {
      header: 'Valor Unit',
      accessor: 'unitValue' as const,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      header: 'Total',
      accessor: 'totalValue' as const,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      header: 'ICMS',
      accessor: 'icmsValue' as const,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      header: 'PIS',
      accessor: 'pisValue' as const,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      header: 'COFINS',
      accessor: 'cofinsValue' as const,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      header: 'IPI',
      accessor: 'ipiValue' as const,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
    {
      header: 'ISS',
      accessor: 'issValue' as const,
      align: 'right' as const,
      render: (value: number) => formatCurrency(value),
    },
  ];

  /* ── Render: Loading ── */

  if (isLoading) {
    return (
      <div className="fiscal-detail-page">
        <div className="mb-lg">
          <Button variant="ghost" onClick={() => navigate({ to: '/fiscal/invoices' })}>
            &larr; Voltar para lista
          </Button>
        </div>
        <Card>
          <div className="loading-container">
            <LoadingSkeleton width="60%" height="24px" />
            <div style={{ height: '0.5rem' }} />
            <LoadingSkeleton width="40%" height="16px" />
            <div style={{ height: '1rem' }} />
            <LoadingSkeleton width="100%" height="120px" />
            <div style={{ height: '1rem' }} />
            <LoadingSkeleton width="100%" height="80px" />
          </div>
        </Card>
      </div>
    );
  }

  /* ── Render: Error ── */

  if (error) {
    const errorMessage = error instanceof ApiError ? error.message : 'Erro ao carregar documento fiscal';

    return (
      <div className="fiscal-detail-page">
        <div className="mb-lg">
          <Button variant="ghost" onClick={() => navigate({ to: '/fiscal/invoices' })}>
            &larr; Voltar para lista
          </Button>
        </div>
        <Card>
          <div className="error-container">
            <p className="error-title">Erro ao carregar documento fiscal</p>
            <p className="error-message">{errorMessage}</p>
            <Button variant="primary" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  /* ── Render: Not found ── */

  if (!invoiceData) {
    return (
      <div className="fiscal-detail-page">
        <div className="mb-lg">
          <Button variant="ghost" onClick={() => navigate({ to: '/fiscal/invoices' })}>
            &larr; Voltar para lista
          </Button>
        </div>
        <Card>
          <div className="error-container">
            <p className="error-title">Documento fiscal não encontrado</p>
            <p className="error-message">
              O documento solicitado não existe ou foi removido.
            </p>
            <Button variant="primary" onClick={() => navigate({ to: '/fiscal/invoices' })}>
              Voltar para lista
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  /* ── Render: Detail ── */

  const totals = invoiceData.totals;

  const totalColumns = [
    { label: 'Valor Total', value: totals.totalValue },
    { label: 'Desconto', value: totals.discount },
    { label: 'Frete', value: totals.shipping },
    { label: 'ICMS', value: totals.icmsTotal },
    { label: 'PIS', value: totals.pisTotal },
    { label: 'COFINS', value: totals.cofinsTotal },
    { label: 'IPI', value: totals.ipiTotal },
    { label: 'ISS', value: totals.issTotal },
  ];

  return (
    <div className="fiscal-detail-page">
      {/* ─── Header ─── */}
      <div className="mb-sm">
        <Button variant="ghost" onClick={() => navigate({ to: '/fiscal/invoices' })}>
          &larr; Voltar para lista
        </Button>
      </div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            {typeLabel[invoiceData.type] ?? invoiceData.type}
            {/* Numero e serie saem da chave de acesso (layout da NF-e). */}
            {invoiceNumber(invoiceData.accessKey) !== null
              ? ` nº ${invoiceNumber(invoiceData.accessKey)}`
              : ''}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            {invoiceSeries(invoiceData.accessKey) !== null
              ? `Série ${invoiceSeries(invoiceData.accessKey)} · `
              : ''}
            Emitido em {formatDate(invoiceData.emissionDate)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          {canCancel && (
            <Button variant="danger" onClick={() => setShowCancelModal(true)}>
              Cancelar documento
            </Button>
          )}
          <Button variant="secondary" disabled>
            Download XML
          </Button>
          <Button variant="secondary" disabled>
            Download DANFE
          </Button>
        </div>
      </div>

      {/* ─── Document Info Card ─── */}
      <Card className="detail-card document-info-card">
        <div className="doc-header">
          <div className="doc-badges">
            <Badge variant="info">{typeLabel[invoiceData.type] ?? invoiceData.type}</Badge>
            <Badge variant={statusVariant[invoiceData.status] ?? 'neutral'}>
              {statusLabel[invoiceData.status] ?? invoiceData.status}
            </Badge>
          </div>
        </div>

        <div className="access-key-section">
          <label className="field-label">Chave de Acesso</label>
          <div className="access-key-value">
            {invoiceData.accessKey ? (
              <>
                <code className="access-key-text">{formatAccessKey(invoiceData.accessKey)}</code>
                <button
                  className="copy-btn"
                  onClick={() => {
                    const chave = invoiceData.accessKey;
                    if (!chave) return;
                    navigator.clipboard.writeText(chave).then(() => {
                      addToast('Chave de acesso copiada', 'success');
                    });
                  }}
                  title="Copiar chave de acesso"
                  aria-label="Copiar chave de acesso"
                >
                  Copiar
                </button>
              </>
            ) : (
              /* Nota nao autorizada nao tem chave: o botao Copiar copiaria
                 "null". O estado real e "ainda sem chave". */
              <code className="access-key-text access-key-text--empty">
                ainda sem chave — a nota não foi autorizada
              </code>
            )}
          </div>
        </div>

        {invoiceData.protocolNumber && (
          <div className="protocol-section">
            <label className="field-label">Protocolo</label>
            <span className="protocol-value">{invoiceData.protocolNumber}</span>
          </div>
        )}

        {invoiceData.rejectionReason && (
          <div className="rejection-section">
            <label className="field-label">Motivo da rejeição</label>
            <span className="rejection-value">{invoiceData.rejectionReason}</span>
          </div>
        )}

        <div className="dates-section">
          <div className="date-item">
            <label className="field-label">Data de emissão</label>
            <span className="date-value">{formatDate(invoiceData.emissionDate)}</span>
          </div>
          {invoiceData.authorizationDate && (
            <div className="date-item">
              <label className="field-label">Data de autorização</label>
              <span className="date-value">{formatDateTime(invoiceData.authorizationDate)}</span>
            </div>
          )}
        </div>
      </Card>

      {/* ─── Emitter / Destinatary ─── */}
      <Card className="detail-card">
        <CardHeader>Emitente & Destinatário</CardHeader>
        <CardBody>
          <div className="parties-grid">
            <div className="party-column">
              <h3 className="party-title">Emitente</h3>
              <p className="party-name">{invoiceData.emitter.companyName}</p>
              <p className="party-doc">
                CNPJ: <strong>{formatCnpj(invoiceData.emitter.cnpj)}</strong>
              </p>
            </div>
            <div className="party-column">
              <h3 className="party-title">Destinatário</h3>
              <p className="party-name">{invoiceData.destinatary.name}</p>
              {invoiceData.destinatary.cnpj ? (
                <p className="party-doc">
                  CNPJ: <strong>{formatCnpj(invoiceData.destinatary.cnpj)}</strong>
                </p>
              ) : invoiceData.destinatary.cpf ? (
                <p className="party-doc">
                  CPF: <strong>{formatCpf(invoiceData.destinatary.cpf)}</strong>
                </p>
              ) : (
                <p className="party-doc">Sem documento</p>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ─── Items Table ─── */}
      <Card className="detail-card">
        <CardHeader>Itens</CardHeader>
        <CardBody>
          <Table
            columns={itemsColumns}
            data={invoiceData.items}
            emptyMessage="Nenhum item encontrado"
          />
          {/* Footer with totals */}
          <div className="items-total-footer">
            <div className="items-total-label">Valor total dos itens</div>
            <div className="items-total-value">
              {formatCurrency(
                invoiceData.items.reduce((sum, item) => sum + item.totalValue, 0),
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ─── Totals Grid ─── */}
      <Card className="detail-card">
        <CardHeader>Totais</CardHeader>
        <CardBody>
          <div className="totals-grid">
            {totalColumns.map((col) => (
              <div key={col.label} className="total-item">
                <span className="total-item-label">{col.label}</span>
                <span
                  className={`total-item-value${col.value < 0 ? ' total-item-value--negative' : ''}`}
                >
                  {formatCurrency(col.value)}
                </span>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* ─── Status Timeline ─── */}
      <Card className="detail-card">
        <CardHeader>Histórico de eventos</CardHeader>
        <CardBody>
          {invoiceData.events && invoiceData.events.length > 0 ? (
            <div className="timeline">
              {[...invoiceData.events]
                .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
                .map((event, index) => (
                  <div
                    key={event.id}
                    className={`timeline-item${index === 0 ? ' timeline-item--latest' : ''}`}
                  >
                    <div className="timeline-marker" />
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <Badge variant="info" size="sm">
                          {event.tipo}
                        </Badge>
                        <span className="timeline-date">{formatDateTime(event.data)}</span>
                      </div>
                      <p className="timeline-description">{event.descricao}</p>
                      <span className="timeline-user">{event.usuario}</span>
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="no-events">Nenhum evento registrado</p>
          )}
        </CardBody>
      </Card>

      {/* ─── Cancel Modal ─── */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => {
          if (!cancelMutation.isPending) {
            setShowCancelModal(false);
            setCancelReason('');
            setCancelJustification('');
          }
        }}
        title="Cancelar documento fiscal"
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowCancelModal(false);
                setCancelReason('');
                setCancelJustification('');
              }}
              disabled={cancelMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleCancelConfirm}
              loading={cancelMutation.isPending}
              disabled={!cancelReason || !cancelJustification.trim()}
            >
              Confirmar cancelamento
            </Button>
          </>
        }
      >
        <div className="cancel-modal-body">
          <p className="cancel-modal-info">
            Tem certeza que deseja cancelar este documento fiscal? Esta ação não pode ser desfeita.
          </p>

          <div className="cancel-form-group">
            <Select
              label="Motivo do cancelamento"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Selecione um motivo..."
            >
              {cancelReasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="cancel-form-group">
            <Textarea
              label="Justificativa"
              value={cancelJustification}
              onChange={(e) => setCancelJustification(e.target.value)}
              placeholder="Descreva o motivo detalhado do cancelamento..."
              rows={4}
              textareaSize="md"
            />
          </div>
        </div>
      </Modal>

      <style>{`
        .fiscal-detail-page {
          padding: 1.5rem;
        }

        .header-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .detail-card {
          margin-bottom: 1.25rem;
        }

        /* ── Document Info ── */

        .doc-header {
          margin-bottom: 1rem;
        }

        .doc-badges {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .access-key-section {
          margin-bottom: 1rem;
        }

        .access-key-value {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-top: 0.25rem;
        }

        .access-key-text--empty { color: #74777f; font-style: normal; }
        .access-key-text {
          font-family: 'Courier New', Courier, monospace;
          font-size: 1rem;
          letter-spacing: 0.05em;
          color: #002045;
          background: #f2f4f6;
          padding: 0.5rem 0.75rem;
          border-radius: 4px;
          border: 1px solid #d6e3ff;
          word-break: break-all;
        }

        .copy-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.375rem 0.75rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: #002045;
          background: transparent;
          border: 1px solid #002045;
          border-radius: 4px;
          cursor: pointer;
          transition: background 0.15s ease, color 0.15s ease;
          font-family: inherit;
          white-space: nowrap;
        }

        .copy-btn:hover {
          background: #002045;
          color: #ffffff;
        }

        .protocol-section,
        .rejection-section {
          margin-bottom: 1rem;
        }

        .protocol-value {
          display: inline-block;
          margin-top: 0.25rem;
          font-weight: 600;
          color: #191c1e;
        }

        .rejection-value {
          display: inline-block;
          margin-top: 0.25rem;
          color: #ba1a1a;
          font-size: 0.875rem;
        }

        .dates-section {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .date-item {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
        }

        .date-value {
          font-size: 0.9375rem;
          color: #191c1e;
        }

        .field-label {
          font-size: 12px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          color: #43474e; font-family: 'JetBrains Mono', ui-monospace, monospace;}

        /* ── Parties ── */

        .parties-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
        }

        .party-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #002045;
          margin: 0 0 0.5rem;
          padding-bottom: 0.375rem;
          border-bottom: 2px solid #d6e3ff;
        }

        .party-name {
          font-size: 1rem;
          font-weight: 600;
          color: #191c1e;
          margin: 0 0 0.375rem;
        }

        .party-doc {
          font-size: 0.875rem;
          color: #43474e;
          margin: 0;
        }

        /* ── Items Footer ── */

        .items-total-footer {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 1rem;
          padding: 0.75rem 1rem;
          margin-top: 0.5rem;
          border-top: 2px solid #e0e3e5;
          background: #f2f4f6;
          border-radius: 0 0 8px 8px;
        }

        .items-total-label {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #43474e;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .items-total-value {
          font-size: 1rem;
          font-weight: 700;
          color: #191c1e;
        }

        /* ── Totals Grid ── */

        .totals-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }

        .total-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          padding: 0.75rem 1rem;
          background: #f2f4f6;
          border-radius: 4px;
          border: 1px solid #eceef0;
        }

        .total-item-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #74777f;
        }

        .total-item-value {
          font-size: 1.125rem;
          font-weight: 700;
          color: #191c1e;
        }

        .total-item-value--negative {
          color: #ba1a1a;
        }

        /* ── Timeline ── */

        .timeline {
          position: relative;
          padding-left: 1.5rem;
        }

        .timeline::before {
          content: '';
          position: absolute;
          left: 0.375rem;
          top: 0.5rem;
          bottom: 0.5rem;
          width: 2px;
          background: #e0e3e5;
        }

        .timeline-item {
          position: relative;
          padding-bottom: 1.5rem;
        }

        .timeline-item:last-child {
          padding-bottom: 0;
        }

        .timeline-marker {
          position: absolute;
          left: -1.5rem;
          top: 0.375rem;
          width: 0.75rem;
          height: 0.75rem;
          border-radius: 50%;
          background: #e0e3e5;
          border: 2px solid #ffffff;
          z-index: 1;
        }

        .timeline-item--latest .timeline-marker {
          background: #002045;
          box-shadow: 0 0 0 3px rgba(0, 32, 69, 0.15);
        }

        .timeline-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.25rem;
          flex-wrap: wrap;
        }

        .timeline-date {
          font-size: 0.75rem;
          color: #74777f;
        }

        .timeline-description {
          margin: 0 0 0.125rem;
          font-size: 0.875rem;
          color: #191c1e;
          line-height: 1.4;
        }

        .timeline-user {
          font-size: 0.75rem;
          color: #74777f;
        }

        .no-events {
          text-align: center;
          color: #74777f;
          font-size: 0.875rem;
          padding: 1.5rem 0;
          margin: 0;
        }

        /* ── Loading ── */

        .loading-container {
          padding: 1rem 0;
        }

        /* ── Error / Not Found ── */

        .error-container {
          padding: 3rem 2rem;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }

        .error-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #ba1a1a;
          margin: 0;
        }

        .error-message {
          font-size: 0.875rem;
          color: #74777f;
          margin: 0;
        }

        /* ── Cancel Modal ── */

        .cancel-modal-body {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .cancel-modal-info {
          margin: 0;
          font-size: 0.875rem;
          color: #43474e;
          line-height: 1.5;
        }

        .cancel-form-group {
          display: flex;
          flex-direction: column;
        }

        /* ── Responsive ── */

        @media (max-width: 768px) {
          .fiscal-detail-page {
            padding: 1rem;
          }

          .parties-grid {
            grid-template-columns: 1fr;
            gap: 1.25rem;
          }

          .header-actions {
            width: 100%;
          }

          .header-actions > * {
            flex: 1;
          }

          .totals-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .dates-section {
            flex-direction: column;
            gap: 0.75rem;
          }

          .access-key-value {
            flex-direction: column;
            align-items: stretch;
          }

          .access-key-text {
            font-size: 0.8125rem;
            word-break: break-all;
          }
        }
      `}</style>
    </div>
  );
}
